import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import withLayout from 'hocs/withLayout';
import { Sidebar } from 'layouts';
import { PageHeader, SectionHeader } from 'molecules';
import { PageProps } from 'types/page';
import { Seo } from '@elevator/ui';
import useStyles from './styles';

type SeoOGType = 'website' | 'article' | 'profile';

interface SeoData {
  baseUrl: string;
  title: string;
  description?: string;
  path: string;
  article?: {
    tags?: string[];
  };
}

type SeoPropTypes = {
  type: SeoOGType;
  data: SeoData;
};

/**
 * Section Component
 *
 * Wraps content sections with consistent styling.
 */
interface SectionProps {
  header: string;
  children: React.ReactNode;
}

const Section = ({ header, children }: SectionProps): React.JSX.Element => {
  const { classes } = useStyles();
  return (
    <Stack className={classes.paragraphContainer} spacing={0.5} alignItems="stretch">
      <SectionHeader
        title={header}
        titleVariant="subtitle1"
        titleComponent="h2"
        divider={false}
      />
      <Box component="div">{children}</Box>
    </Stack>
  );
};

/**
 * ExternalLink Component
 *
 * Renders an external link with proper security attributes.
 */
interface ExternalLinkProps {
  href: string;
  children: string;
}

const ExternalLink = ({ href, children }: ExternalLinkProps): React.JSX.Element => {
  const { classes } = useStyles();
  return (
    <a href={href} target={'_blank'} rel={'noreferrer'} className={classes.externalLink}>
      {children}
    </a>
  );
};

/**
 * Page Component
 *
 * Shared layout for static content pages, optionally with a sidebar.
 * Provides consistent structure: title, divider, and content area.
 *
 * Features:
 * - Optional sidebar (social, ad, trending) — omit with `showSidebar={false}`
 * - SEO metadata support
 * - PageHeader title with optional link (`titleAsLink` → `titleHref`) and divider
 * - Content column renders as `<main>` (sidebar stays `<aside>` in `Sidebar`)
 * - Content area for page-specific content
 * - Helper components (Section, ExternalLink, TextHeader)
 *
 * Used in:
 * - /contact page
 * - /how-to-report-scammers page
 *
 * @param props - Component props
 * @param props.title - Page title text
 * @param props.titleAsLink - Optional link URL for title (if provided, title becomes a link)
 * @param props.seoData - SEO metadata object
 * @param props.children - Page content
 * @param props.showSidebar - Whether to render the sidebar column (default: true)
 * @param props.showTrendingPosts - Whether to show trending posts in sidebar (default: false)
 * @param props.showSidebarAd - Whether to show the sidebar banner ad (default: true)
 * @param props.socialMediaVariant - Size variant for social media icons (default: 'large')
 * @returns Page component with optional sidebar and content structure
 */
const Page = (props: PageLayoutProps): React.JSX.Element => {
  const { classes } = useStyles();
  const {
    title,
    titleAsLink,
    seoData,
    children,
    showSidebar = true,
    showTrendingPosts = false,
    socialMediaVariant = 'large',
    showSidebarAd = true,
  } = props;

  const mainColumn = (
    <Stack
      component="main"
      className={classes.container}
      data-layout="page"
      width="100%"
      direction="column"
      spacing={3}
    >
      <PageHeader title={title} titleHref={titleAsLink} titleComponent="h1" divider />
      <Stack component="div" spacing={2} className={classes.pageSections}>
        {children}
      </Stack>
    </Stack>
  );

  return (
    <>
      {seoData && <Seo {...seoData} />}
      {showSidebar ? (
        <Sidebar
          {...props}
          showTrendingPosts={showTrendingPosts}
          socialMediaVariant={socialMediaVariant}
          showSidebarAd={showSidebarAd}
        >
          {mainColumn}
        </Sidebar>
      ) : (
        mainColumn
      )}
    </>
  );
};

export interface PageLayoutProps extends PageProps {
  /** Page title text */
  title: string;
  /** Optional link URL for title (if provided, title becomes a link) */
  titleAsLink?: string;
  /** SEO metadata object */
  seoData?: SeoPropTypes;
  /** Page content */
  children: React.ReactNode;
  /** When false, main content only (no sidebar). Default: true */
  showSidebar?: boolean;
  /** Whether to show trending posts in sidebar (default: false) */
  showTrendingPosts?: boolean;
  /** Whether to show the sidebar banner ad (default: true) */
  showSidebarAd?: boolean;
  /** Size variant for social media icons (default: 'large') */
  socialMediaVariant?: 'small' | 'medium' | 'large';
}

// Export helper components for use in pages
export { Section, ExternalLink };

export default withLayout(Page, {
  contentWidth: 'md',
  gutter: true,
  margin: true,
});
